import { ServiceWithPricing } from '@/util/types/serverTypes';
import {
  Bundle,
  DiscountCode,
} from '@/util/types/serverTypeCollection/discounts';

/**
 * Annotates services with bundle and discount code metadata
 * 
 * Adds metadata to services indicating which bundles or discount codes apply.
 * This metadata is used for:
 * - Displaying discount indicators in the UI
 * - Audit trails for pricing decisions
 * - Reporting and analytics
 * 
 * @param services - Services with calculated pricing
 * @param bundle - Active bundle (if any)
 * @param discountCode - Active discount code (if any)
 * @param selectedServiceIds - Array of selected service IDs
 * @returns Services with bundle/discount metadata annotations
 */
export function annotateBundleMetadata(
  services: ServiceWithPricing[],
  bundle: Bundle | null,
  discountCode: DiscountCode | null,
  selectedServiceIds: string[]
): ServiceWithPricing[] {
  // Build set of services that are part of the bundle
  const bundleServiceIds = new Set<string>(
    bundle
      ? [
          ...(bundle.items?.map((i) => i._serviceId) || []),
          ...(bundle._primaryServiceId || []),
        ]
      : []
  );

  // Find which primary service triggered the bundle
  const triggeringPrimaryId = bundle
    ? selectedServiceIds.find((id) =>
        (bundle._primaryServiceId || []).includes(id)
      )
    : undefined;

  return services.map((svc) => {
    const participatesInBundle =
      !!bundle &&
      (bundleServiceIds.has(svc._id) || svc._id === triggeringPrimaryId);
    const hasDiscountCode = !!discountCode;

    return {
      ...svc,
      ...(participatesInBundle
        ? {
            appliedBundleId: bundle!._id,
            bundleName: bundle!.name,
          }
        : {}),
      ...(hasDiscountCode
        ? {
            appliedDiscountCodeId: discountCode!._id,
            discountCodeName: discountCode!.code,
          }
        : {}),
    };
  });
}

